import { useAuth, useFirestore, useFirestoreDocData, useStorage, useUser } from "reactfire";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, doc, onSnapshot, orderBy, query, setDoc, Timestamp } from "firebase/firestore";
import { Link, useParams } from "react-router-dom";
import { BreadcrumbItem, Breadcrumbs, Button, Code, Input, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/react";
import { useEffect, useRef, useState } from "react";
import { MdCheckBox } from "react-icons/md";
import { v4 as uuidv4 } from 'uuid';
import { FileType } from "../../types/types";
import Chat from "../jobs/jobchat";

export const KnowledgeBase = () => {
    const { id } = useParams();
    const auth = useAuth();
    const user = useUser();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const storage = useStorage();
    const firestore = useFirestore();
    const { data: kbase } = useFirestoreDocData(doc(firestore, 'kbases/' + id), { idField: 'id' });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [error, setError] = useState<any>();
    const [files, setFiles] = useState<any>();
    const [filteredFiles, setFilteredFiles] = useState<any>();
    const [filterTerm, setFilterTerm] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {

            if (auth.currentUser) {
                const q = query(collection(firestore, `kbases/${id}/files`), orderBy('createdAt', 'desc'));
                console.log('loading files');
                const unsubscribe = onSnapshot(q, (querySnapshot) => {
                    const docs: Array<any> = [];
                    querySnapshot.forEach((doc) => {
                        docs.push({ ...doc.data(), id: doc.id });
                    });
                    setFiles(docs);
                });
            }

            console.log('Loaded files');
        }
        fetchData();
    }, []);

    useEffect(() => {
        if (filterTerm) {
            const filtered = files?.filter((file: any) => file.name.toLowerCase().includes(filterTerm.toLowerCase()));
            setFilteredFiles(filtered);
        } else {
            setFilteredFiles(files);
        }
    }, [filterTerm, files]);

    if (!kbase) {
        return (
            <div className="flex w-full h-full justify-center items-center">
                <div className="text-2xl">Loading...</div>
            </div>
        )
    }

    const uploadFile = (file: any) => {
        const fileRef = ref(storage, `/kbases/${auth.currentUser?.uid}/${id}/${file.name}`);
        const timestamp = Timestamp.fromDate(new Date());

        uploadBytes(fileRef, file).then(async (snapshot) => {
            const fileId = uuidv4();
            const newUploadFile: FileType = {
                id: fileId,
                path: fileRef.fullPath,
                name: file.name,
                downloadUrl: await getDownloadURL(fileRef),
                owner: auth.currentUser?.uid || '',
                indexing: true,
                createdAt: timestamp,
                deleted: false
            }

            setDoc(doc(firestore, `kbases/${id}/files/${fileId}`), newUploadFile, { merge: true });
            setDoc(doc(firestore, `files/${fileId}`), newUploadFile, { merge: true })
        });
    }

    const handleFileUpload = (e: any) => {
        setIsProcessing(true);
        for (let index = 0; index < e.target.files.length; index++) {
            const element = e.target.files[index];
            uploadFile(element);
        }
        setIsProcessing(false);
        console.log('Uploaded a blob or file!');
    };

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div className="">
            <div className="flex flex-row items-end justify-between">
                <div className=" ">
                    <Breadcrumbs>
                        <BreadcrumbItem><Link to="/kbases">DoxBase</Link></BreadcrumbItem>
                        <BreadcrumbItem>Knowledge Base</BreadcrumbItem>
                    </Breadcrumbs>
                    <div className="text-2xl mt-4 font-bold">{kbase?.name} <Code size="sm">{id}</Code></div>
                    <div className="text-md">{kbase?.description} </div>
                </div>

            </div>
            <div className="flex w-full flex-col mt-4 ">
                <div className="grid grid-cols-2 min-h-[70vh] max-h-[70vh]">
                    <div className="flex flex-col border-r-1 pr-4">
                        <div className="flex flex-row mb-4 gap-4">
                            <Input
                                name="search"
                                variant="bordered"
                                placeholder="Search"
                                onChange={(e) => setFilterTerm(e.target.value)}
                            />
                            <Button color="primary" onPress={() => fileInputRef.current?.click()}>Upload</Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: "none" }}
                                onChange={handleFileUpload}
                                multiple
                            />
                        </div>
                        <div className="flex w-full ">
                            <Table
                                aria-label="Files"
                            >
                                <TableHeader>
                                    <TableColumn>Name</TableColumn>
                                    <TableColumn>Uploaded At</TableColumn>
                                    <TableColumn>Status</TableColumn>
                                </TableHeader>
                                <TableBody
                                    emptyContent={"No rows to display"}>
                                    {filteredFiles?.map((file: any) => (

                                        <TableRow key={file.id} >
                                            <TableCell>{file.name}</TableCell>
                                            <TableCell>{dateFormatter.format(file.createdAt.toDate())}</TableCell>
                                            <TableCell>{file.indexing ? <Spinner size="sm" /> : <MdCheckBox />}</TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    <div className="flex flex-col pl-4 h-full">
                        {!files || files.length == 0 && (
                            <div className="flex items-center align-middle justify-center">
                                No files added
                            </div>
                        )}
                        {files && files.length > 0 && (
                            <>
                                <div className="text-lg font-bold">Explore via Chat</div>
                                <Chat jobId={kbase.jobId} />
                            </>
                        )}
                    </div>

                </div>
            </div>
        </div >
    );
}